<template>
    <div v-if="hasAccess">
        <top-nav-bar goto="/"></top-nav-bar>

        <div class="max-w-sm mx-auto p-4 pb-0">
            <form-group label="" name="period">
                <div class="timesheet-period-group input-group flex">
                    <button class="text-white period-arrow-icon"
                            :disabled="loading"
                            :class="{
                                'bg-grey': loading,
                                'bg-blue': !loading
                            }"
                            @click.prevent="onClickLeftArrowButton">
                        <i class="far fa-angle-left"></i>
                    </button>
                    <input
                        name="period"
                        type="text"
                        readonly
                        :value="period"
                        ref="periodField"
                        class="input text-center">
                    <button class="text-white period-arrow-icon"
                            :class="{
                                'bg-grey': disabledPeriodRightArrow || loading,
                                'bg-blue': !disabledPeriodRightArrow && !loading
                            }"
                            :disabled="disabledPeriodRightArrow || loading"
                            @click.prevent="onClickRightArrowButton">
                        <i class="far fa-angle-right"></i>
                    </button>
                </div>
            </form-group>
        </div>

        <div v-if="loading" class="h-full flex items-center justify-center">
            <div class="spinner-blue"></div>
        </div>

        <template v-else>
            <list v-if="entries.length">
                <list-item class="text-base sm:text-xl"
                           v-for="(entry, index) in entries"
                           url="#"
                           :key="index"
                           @click.native.prevent
                           @click="navigateTo(entry)">
                    <template slot="icon">
                        <span class="relative">
                            <span v-if="entry.status" class="text-center w-full">
                                <i class="far" :class="findObjectStatusIconStyles(entry.status)"></i>
                            </span>
                            <span v-if="entry.id && $hasNotification('timesheets', entry.id).length > 0"
                                  class="h-3 w-3 flex -ml-2 -mt-6 absolute pin-t pin-l bg-red rounded-full"
                            ></span>
                        </span>
                    </template>
                    <template slot="title">
                        {{ formatDate(entry.date, 'D MMM') }}
                    </template>
                    <template slot="additional">
                        <span v-if="entry.intervals.length"
                              class="font-heading uppercase text-xs font-bold text-grey">
                            {{ getTotalDuration(entry.intervals) }}
                        </span>
                    </template>
                    <template slot="status-small">
                        <span v-if="entry.status" class="font-heading uppercase text-xs font-bold"
                              :class="findObjectStatusStyles(entry.status)">
                            {{entry.status.status}}
                        </span>
                    </template>
                </list-item>
            </list>

            <empty :visible="!entries.length">
                You have not submitted any entries.
            </empty>
        </template>
    </div>
</template>

<script>
    import InteractsWithDates from '@/mixins/InteractsWithDates';
    import NeedsTimesheetsDuration from '@/mixins/NeedsTimesheetsDuration';
    import ShowObjectStatus from '@/mixins/ShowObjectStatus';
    import CanAccessView from '@/mixins/CanAccessView';
    import moment from "moment";

    export default {
        mixins: [InteractsWithDates, ShowObjectStatus, CanAccessView, NeedsTimesheetsDuration],

        data() {
            return {
                loading: true,
                entries: [],
                period: moment().format('MMM Y')
            }
        },

        mounted() {
            this.getEntries();
        },

        computed: {
            disabledPeriodRightArrow() {
                return this.period === moment().format('MMM Y');
            },
        },

        methods: {
            getEntries() {
                this.loading = true;
                this.$api('timesheets').get('', {
                    params: {
                        period: moment(this.period, 'MMM Y').format('YYYY-MM')
                    }
                }).then(({data}) => {
                    const entries = [];
                    let dayNumber = 1;

                    while (dayNumber <= parseInt(moment(this.period, 'MMM Y').daysInMonth())) {
                        let entry = {
                            id: null,
                            date: moment(moment(this.period, 'MMM Y'))
                                .date(dayNumber).format('YYYY-MM-DD'),
                            intervals: [],
                            rest_day: false,
                            status: null
                        }
                        const entryData = data.data.find(item => item.date === entry.date);

                        if (!!entryData) {
                            entry = entryData;
                        }

                        entries.push(entry);

                        if (moment().format('YYYY-MM-DD') === entry.date) {
                            break
                        }

                        dayNumber++;
                    }

                    this.entries = entries;
                }).finally(() => this.loading = false);
            },

            onClickLeftArrowButton() {
                this.period = moment(this.period, 'MMM Y')
                    .subtract(1, 'months').format('MMM Y');

                this.getEntries();
            },

            onClickRightArrowButton() {
                if (this.period === moment().format('MMM Y')) {
                    return;
                }

                this.period = moment(this.period, 'MMM Y')
                    .add(1, 'months').format('MMM Y');

                this.getEntries();
            },

            navigateTo(entry) {
                let routerPath = '/timesheets/' + entry.date + '/add';

                if (entry.id) {
                    this.processNotifications(entry);
                    routerPath = '/timesheets/' + entry.id;
                }

                this.$router.push(routerPath);
            },

            processNotifications(entry) {
                let notifications = this.$hasNotification('timesheets', entry.id);

                if (notifications.length > 0) {
                    notifications.forEach(notification => this.$notificationRead(notification));
                }
            }
        },
    }
</script>

<style lang="scss" scoped>
.timesheet-period-group {
    .period-arrow-icon {
        font-size: 1rem;
        padding: 0 0.75rem;
    }

    .input:focus {
        outline:none;
        outline-width: 0;
        box-shadow: none;
        -moz-box-shadow: none;
        -webkit-box-shadow: none;
    }
}
</style>
